import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import PageHeader, { PageBody, Card, Badge } from "../components/Shared";
import { toast } from "sonner";
import {
  UserPlus, ChartLineUp, Trophy, Lightning, BookOpen,
  PaperPlaneTilt, Sparkle, ArrowRight,
} from "@phosphor-icons/react";

const STAGE_LABELS = {
  new_lead: { label: "Nuevo lead", tone: "neutral", desc: "Captura recién creada" },
  candidate: { label: "Candidato", tone: "warning", desc: "Mostró interés inicial" },
  founder_confirmed: { label: "Confirmado", tone: "terracotta", desc: "Aceptó ser fundador" },
  founder_active: { label: "Activo", tone: "forest", desc: "Participa en dinámicas" },
  founder_core: { label: "Núcleo", tone: "forest", desc: "Núcleo duro de la comunidad" },
  leader_potential: { label: "Líder potencial", tone: "terracotta", desc: "Articula comunidad" },
  founder_cold: { label: "Frío", tone: "neutral", desc: "Baja participación" },
  dormant: { label: "Dormante", tone: "signal", desc: "Sin respuesta 30+ días" },
  not_interested: { label: "No interesado", tone: "signal", desc: "Declinó explícitamente" },
};

export default function FounderDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCapture, setShowCapture] = useState(false);
  const [showDynamic, setShowDynamic] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/founder/dashboard");
      setData(data);
    } catch (e) { /* */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const generateDigest = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/founder/digest/generate", { theme: null });
      if (d.error) toast.error(d.error);
      else toast.success(`Digest generado · basado en ${d.based_on_messages} respuestas`);
      load();
    } catch (e) { toast.error("Error generando digest"); } finally { setBusy(false); }
  };

  if (loading || !data) return <div className="p-10 text-carbon-500">Cargando...</div>;

  if (data.error === "pipeline_missing") {
    return (
      <PageBody>
        <Card className="p-10 text-center max-w-xl mx-auto mt-10">
          <div className="section-label mb-2">Founder Pipeline · sin inicializar</div>
          <h2 className="font-heading text-2xl mb-3">No hay pipeline founder en la base de datos</h2>
          <p className="text-sm text-carbon-600 leading-relaxed mb-4">
            Esto pasa si borraste toda la data o estás arrancando desde cero.
            Necesitas re-correr el seed que crea los pipelines (founder + ambassador),
            agentes IA y stages. El comando es:
          </p>
          <pre className="bg-page border border-carbon-200 rounded p-3 text-xs font-mono text-left mb-3">cd /app/backend && python3 seed.py</pre>
          <p className="text-xs text-carbon-500">O contacta al admin para que lo ejecute en producción.</p>
        </Card>
      </PageBody>
    );
  }

  return (
    <>
      <PageHeader
        label="Pre-launch · captación de fundadores"
        title="Founder Pipeline"
        description="Captación, conversación IA, scoring de liderazgo y dinámicas comunitarias. Reusa AI Memory, scheduler y campaign engine."
      >
        <button onClick={() => setShowDynamic(true)} data-testid="run-dynamic-btn" className="bg-forest text-white px-4 py-2 rounded text-sm flex items-center gap-2 hover:bg-forest/90">
          <Sparkle size={14} /> Lanzar dinámica
        </button>
        <button onClick={() => setShowCapture(true)} data-testid="open-capture-btn" className="bg-terracotta text-white px-4 py-2 rounded text-sm flex items-center gap-2 hover:bg-terracotta-hover">
          <UserPlus size={14} /> Simular Meta Lead
        </button>
      </PageHeader>

      <PageBody>
        {/* Stage funnel */}
        <Card className="p-6 mb-6" testid="founder-stage-funnel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="section-label">Funnel</div>
              <h2 className="font-heading text-xl">Distribución por stage · {data.total_in_pipeline} en pipeline</h2>
            </div>
            <Badge tone="terracotta">{data.participation_7d} respuestas inbound · 7d</Badge>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-2">
            {Object.entries(data.stage_counts || {}).map(([key, count]) => {
              const meta = STAGE_LABELS[key] || { label: key, tone: "neutral", desc: "" };
              return (
                <div key={key} className="border border-carbon-200 rounded p-3 hover:border-terracotta transition-colors" data-testid={`stage-${key}`}>
                  <div className="text-[10px] uppercase tracking-wider text-carbon-500 mb-1">{meta.label}</div>
                  <div className="font-heading text-2xl font-light tracking-tighter">{count}</div>
                  <div className="text-[10px] text-carbon-400 mt-1 leading-tight">{meta.desc}</div>
                </div>
              );
            })}
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Top leaders */}
          <Card className="p-6 lg:col-span-2" testid="leader-board">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Trophy size={18} className="text-terracotta" />
                <div>
                  <div className="section-label">Leaderboard</div>
                  <h2 className="font-heading text-xl">Líderes potenciales</h2>
                </div>
              </div>
            </div>
            {(data.leaders || []).length === 0 ? (
              <div className="text-sm text-carbon-400 py-6 text-center">Aún sin líderes detectados. Genera conversación con los fundadores activos.</div>
            ) : (
              <div className="space-y-2">
                {(data.leaders || []).map((l, idx) => {
                  const meta = STAGE_LABELS[l.stage_key] || { label: l.stage_key, tone: "neutral" };
                  return (
                    <Link key={l.id} to={`/users/${l.id}`} className="block">
                      <div className="flex items-center gap-4 p-3 border border-carbon-200 rounded hover:border-terracotta transition-colors">
                        <div className="w-8 h-8 bg-surface-alt rounded-full flex items-center justify-center font-heading font-medium text-carbon-700">{idx + 1}</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{l.name}</div>
                          <div className="text-xs text-carbon-500 truncate">{l.origin_country} → {l.current_city || "—"}</div>
                        </div>
                        <Badge tone={meta.tone}>{meta.label}</Badge>
                        <div className="text-right">
                          <div className="text-[10px] text-carbon-500">Liderazgo</div>
                          <div className="font-mono text-sm text-terracotta">{l.leadership_score?.toFixed?.(2) ?? "0.00"}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-[10px] text-carbon-500">Engagement</div>
                          <div className="font-mono text-sm text-forest">{l.engagement_score?.toFixed?.(2) ?? "0.00"}</div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Top territories */}
          <Card className="p-6" testid="founder-top-territories">
            <div className="section-label mb-1">Territorios</div>
            <h2 className="font-heading text-xl mb-4">Más activos</h2>
            {(data.top_territories || []).length === 0 ? (
              <div className="text-sm text-carbon-400">Sin datos territoriales aún.</div>
            ) : (
              <div className="space-y-2">
                {(data.top_territories || []).map((t, idx) => (
                  <div key={`${t.country}-${t.municipality}-${idx}`} className="flex items-center justify-between border border-carbon-200 rounded p-3">
                    <div>
                      <div className="font-medium text-sm">{t.municipality}</div>
                      <div className="text-xs text-carbon-500">{t.country}</div>
                    </div>
                    <div className="font-mono text-lg text-terracotta">{t.count}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Collective digest */}
        <Card className="p-6" testid="collective-digest">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <BookOpen size={18} className="text-forest" />
              <div>
                <div className="section-label">Voz de la comunidad</div>
                <h2 className="font-heading text-xl">Último resumen colectivo</h2>
              </div>
            </div>
            <button onClick={generateDigest} disabled={busy} data-testid="generate-digest-btn" className="bg-terracotta text-white px-4 py-2 rounded text-sm flex items-center gap-2 hover:bg-terracotta-hover disabled:opacity-60">
              <Sparkle size={14} /> {busy ? "Generando..." : "Generar digest"}
            </button>
          </div>
          {!data.latest_digest ? (
            <div className="text-sm text-carbon-400 py-6 text-center">Aún sin digests. Lanza una dinámica, recoge respuestas y genera un resumen anónimo.</div>
          ) : (
            <div className="bg-page border-l-4 border-terracotta p-4 rounded">
              {data.latest_digest.theme && (
                <div className="section-label mb-2">Tema · {data.latest_digest.theme}</div>
              )}
              <p className="text-sm leading-relaxed text-carbon-900 whitespace-pre-wrap">{data.latest_digest.summary}</p>
              <div className="mt-3 text-[10px] font-mono text-carbon-500 flex items-center gap-4">
                <span>{new Date(data.latest_digest.created_at).toLocaleString()}</span>
                <span>· {data.latest_digest.based_on_messages} respuestas reales</span>
                <span>· {data.latest_digest.provider_used}/{data.latest_digest.model_used}</span>
              </div>
            </div>
          )}
          {data.latest_dynamic && (
            <div className="mt-4 pt-4 border-t border-carbon-200 text-xs text-carbon-500">
              <span className="section-label">Última dinámica enviada:</span> "{data.latest_dynamic.question}" · {data.latest_dynamic.sent_to} destinatarios
            </div>
          )}
        </Card>
      </PageBody>

      {showCapture && <MetaLeadModal onClose={() => setShowCapture(false)} onCreated={() => { setShowCapture(false); load(); }} />}
      {showDynamic && <DynamicModal questions={data.available_dynamics || []} onClose={() => setShowDynamic(false)} onSent={() => { setShowDynamic(false); load(); }} />}
    </>
  );
}

function MetaLeadModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", whatsapp: "+1", origin_country: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/founder/capture", form);
      toast.success(`Lead capturado · ${data.public_id} · stage ${data.stage}. Primer contacto agendado.`);
      onCreated();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-carbon-900/60 z-50 flex items-center justify-center p-4">
      <div className="bg-surface border border-carbon-200 rounded-lg w-full max-w-md p-6">
        <div className="section-label mb-1">Meta Lead Form</div>
        <h2 className="font-heading text-2xl mb-1">Simular captura desde Meta</h2>
        <p className="text-xs text-carbon-500 mb-4">Crea/actualiza UnifiedUser, lo coloca en stage <code className="font-mono">new_lead</code> y agenda el primer mensaje IA en menos de 5 min.</p>
        <form onSubmit={submit} className="space-y-3">
          <label className="block"><span className="section-label">Nombre</span>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="capture-name" className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" /></label>
          <label className="block"><span className="section-label">WhatsApp</span>
            <input required value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="capture-whatsapp" className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" /></label>
          <label className="block"><span className="section-label">País de origen</span>
            <input value={form.origin_country} onChange={(e) => setForm({ ...form, origin_country: e.target.value })} className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" placeholder="México, Guatemala, ..." /></label>
          <div className="flex justify-end gap-2 pt-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-carbon-700 hover:bg-carbon-100 rounded">Cancelar</button>
            <button type="submit" disabled={busy} data-testid="capture-submit" className="px-4 py-2 text-sm bg-terracotta text-white rounded hover:bg-terracotta-hover disabled:opacity-60">{busy ? "Enviando..." : "Capturar"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DynamicModal({ questions, onClose, onSent }) {
  const [question, setQuestion] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");  // datetime-local
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/whatsapp/templates", { params: { pipeline: "founder" } })
      .then((r) => {
        const items = Array.isArray(r.data) ? r.data : (r.data.items || []);
        if (!cancelled) setTemplates(items.filter((t) => t.status === "approved"));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const submit = async (custom) => {
    const finalQ = custom ?? (question || null);
    if (scheduledAt && !finalQ && !templateId) {
      toast.error("Para programar necesitas una pregunta o template.");
      return;
    }
    setBusy(true);
    try {
      const payload = { question: finalQ };
      if (templateId) payload.template_id = templateId;
      if (scheduledAt) {
        // datetime-local → asume hora local del navegador; lo convertimos a ISO UTC.
        payload.scheduled_at = new Date(scheduledAt).toISOString();
      }
      const { data } = await api.post("/founder/dynamics/run", payload);
      if (data.scheduled) {
        toast.success(`Dinámica programada para ${new Date(data.scheduled_at).toLocaleString("es-GT")}`);
      } else if (data.error) {
        toast.error(data.error);
      } else if (data.failed > 0 && data.sent === 0) {
        toast.error(`⚠️ Ningún mensaje se entregó (${data.failed} fallos). ${data.first_error || ""}`);
      } else if (data.failed > 0) {
        toast.warning(`Dinámica enviada a ${data.sent}, fallaron ${data.failed}. Primer error: ${data.first_error}`);
      } else {
        toast.success(`Dinámica enviada a ${data.sent} fundadores`);
      }
      onSent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-carbon-900/60 z-50 flex items-center justify-center p-4">
      <div className="bg-surface border border-carbon-200 rounded-lg w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto scrollbar-thin">
        <div className="section-label mb-1">Dinámica</div>
        <h2 className="font-heading text-2xl mb-1">Lanzar pregunta a la comunidad</h2>
        <p className="text-xs text-carbon-500 mb-4">Se envía a fundadores en stages confirmed/active/core/leader_potential.</p>

        {/* Template opcional · solo APROBADAS por Meta */}
        <label className="block mb-3">
          <span className="section-label">Template WhatsApp <span className="text-carbon-400">(opcional · solo aprobadas)</span></span>
          <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} data-testid="dynamic-template-select"
            className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm">
            <option value="">Sin template (formato libre)</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>{t.name} ({t.language})</option>
            ))}
          </select>
          {templates.length === 0 && (
            <p className="text-[10px] text-carbon-400 mt-1">No hay templates founder aprobadas. Aprobalas en /templates.</p>
          )}
        </label>

        {/* Programar opcional */}
        <label className="block mb-4">
          <span className="section-label">Programar envío <span className="text-carbon-400">(opcional)</span></span>
          <input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)}
            data-testid="dynamic-scheduled-at"
            className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" />
          <p className="text-[10px] text-carbon-400 mt-1">Si lo dejás vacío, se envía inmediatamente.</p>
        </label>

        {!scheduledAt && (
          <div className="space-y-2 mb-4">
            <div className="section-label">Sugeridas (envío inmediato)</div>
            {questions.map((q, i) => (
              <button key={i} onClick={() => submit(q)} disabled={busy} className="w-full text-left p-3 border border-carbon-200 rounded hover:border-terracotta hover:bg-page transition-colors text-sm group flex items-center justify-between gap-3">
                <span>{q}</span>
                <ArrowRight size={14} className="text-carbon-400 group-hover:text-terracotta" />
              </button>
            ))}
          </div>
        )}

        <div className="border-t border-carbon-200 pt-4">
          <label className="block"><span className="section-label">{scheduledAt ? "Pregunta a programar" : "O escribe la tuya"}</span>
            <textarea rows={3} value={question} onChange={(e) => setQuestion(e.target.value)} data-testid="dynamic-custom-question"
              className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" />
          </label>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={onClose} className="px-4 py-2 text-sm text-carbon-700 hover:bg-carbon-100 rounded">Cancelar</button>
            <button onClick={() => submit()} disabled={busy || (!question.trim() && !scheduledAt)}
              data-testid="dynamic-submit-btn"
              className="px-4 py-2 text-sm bg-forest text-white rounded hover:bg-forest/90 disabled:opacity-60 flex items-center gap-2">
              <PaperPlaneTilt size={14} /> {busy ? "Enviando..." : (scheduledAt ? "Programar" : "Enviar")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
